import React from "react";
import { motion } from "framer-motion";
import {
  Award,
  BookMarked,
  Globe2,
  MapPin,
  Users,
  FileCheck2,
  Microscope,
  ShieldCheck,
} from "lucide-react";

// ── Animation helpers ──────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0  },
  viewport:    { once: true, amount: 0.15 },
  transition:  { duration: 0.55, ease: "easeOut", delay },
});

// ── Cards Data ─────────────────────────────────────────────────────
const HIGHLIGHTS = [
  {
    icon:    Award,
    tag:     "Milestone",
    title:   "10th Edition",
    desc:    "A decade of bringing together global researchers, engineers and policy makers to advance sustainable civil engineering practices.",
    stat:    "Since 2016",
    statLabel: "Running Annually",
    bg:      "bg-[#111827]",
    iconBg:  "bg-[#F58220]/15",
    iconColor:"text-[#F58220]",
    tagColor: "bg-[#F58220]/15 text-[#F58220]",
    titleColor:"text-white",
    descColor: "text-white/60",
    statColor: "text-white",
    statLabelColor:"text-white/40",
    borderColor:"border-white/10",
    accent: true,
  },
  {
    icon:    BookMarked,
    tag:     "Publication",
    title:   "Springer Proceedings",
    desc:    "Select high-quality papers will be published as SCOPUS Indexed Conference Proceedings by Springer after rigorous peer review.",
    stat:    "SCOPUS",
    statLabel: "Indexed Journal",
    bg:      "bg-white",
    iconBg:  "bg-orange-50",
    iconColor:"text-[#F58220]",
    tagColor: "bg-orange-50 text-[#F58220]",
    titleColor:"text-gray-900",
    descColor: "text-gray-500",
    statColor: "text-gray-900",
    statLabelColor:"text-gray-400",
    borderColor:"border-gray-100",
    accent: false,
  },
  {
    icon:    Globe2,
    tag:     "Scope",
    title:   "International Conference",
    desc:    "Gathering global experts, researchers and industry leaders from across India and international institutions to share cutting-edge research.",
    stat:    "500+",
    statLabel: "Expected Participants",
    bg:      "bg-white",
    iconBg:  "bg-orange-50",
    iconColor:"text-[#F58220]",
    tagColor: "bg-orange-50 text-[#F58220]",
    titleColor:"text-gray-900",
    descColor: "text-gray-500",
    statColor: "text-gray-900",
    statLabelColor:"text-gray-400",
    borderColor:"border-gray-100",
    accent: false,
  },
  {
    icon:    MapPin,
    tag:     "Mode",
    title:   "In-Person Conference",
    desc:    "Conducted exclusively in offline mode at REVA University, Bengaluru — enabling direct networking, collaboration and hands-on engagement.",
    stat:    "Offline",
    statLabel: "Mode Only",
    bg:      "bg-white",
    iconBg:  "bg-orange-50",
    iconColor:"text-[#F58220]",
    tagColor: "bg-orange-50 text-[#F58220]",
    titleColor:"text-gray-900",
    descColor: "text-gray-500",
    statColor: "text-gray-900",
    statLabelColor:"text-gray-400",
    borderColor:"border-gray-100",
    accent: false,
  },
  {
    icon:    FileCheck2,
    tag:     "Review Process",
    title:   "Double-Blind Review",
    desc:    "All submitted papers undergo a strict double-blind peer review by domain expert reviewers followed by Springer's quality checks.",
    stat:    "100%",
    statLabel: "Peer Reviewed",
    bg:      "bg-white",
    iconBg:  "bg-orange-50",
    iconColor:"text-[#F58220]",
    tagColor: "bg-orange-50 text-[#F58220]",
    titleColor:"text-gray-900",
    descColor: "text-gray-500",
    statColor: "text-gray-900",
    statLabelColor:"text-gray-400",
    borderColor:"border-gray-100",
    accent: false,
  },
  {
    icon:    Microscope,
    tag:     "Research",
    title:   "4 Research Tracks",
    desc:    "Covering Materials & Structures, Geotechnical Applications, Water Resources & Environment, and Transportation & Construction Management.",
    stat:    "17+",
    statLabel: "Sub-Topics",
    bg:      "bg-white",
    iconBg:  "bg-orange-50",
    iconColor:"text-[#F58220]",
    tagColor: "bg-orange-50 text-[#F58220]",
    titleColor:"text-gray-900",
    descColor: "text-gray-500",
    statColor: "text-gray-900",
    statLabelColor:"text-gray-400",
    borderColor:"border-gray-100",
    accent: false,
  },
  {
    icon:    Users,
    tag:     "Participation",
    title:   "Open to All",
    desc:    "Academicians, research scholars, postgraduate students, industry professionals and R&D organisations across India are welcome to participate.",
    stat:    "4 +",
    statLabel: "Participant Categories",
    bg:      "bg-white",
    iconBg:  "bg-orange-50",
    iconColor:"text-[#F58220]",
    tagColor: "bg-orange-50 text-[#F58220]",
    titleColor:"text-gray-900",
    descColor: "text-gray-500",
    statColor: "text-gray-900",
    statLabelColor:"text-gray-400",
    borderColor:"border-gray-100",
    accent: false,
  },
  {
    icon:    ShieldCheck,
    tag:     "Accreditation",
    title:   "NAAC A+ University",
    desc:    "Hosted by REVA University — UGC recognised and AICTE approved institution with a sprawling 43-acre green campus in Bengaluru.",
    stat:    "A+",
    statLabel: "NAAC Grade",
    bg:      "bg-[#F58220]",
    iconBg:  "bg-white/20",
    iconColor:"text-white",
    tagColor: "bg-white/20 text-white",
    titleColor:"text-white",
    descColor: "text-white/70",
    statColor: "text-white",
    statLabelColor:"text-white/60",
    borderColor:"border-white/20",
    accent: true,
  },
];

// ─────────────────────────────────────────────────────────────────
export default function KeyHighlights() {
  return (
    <section className="w-full bg-gray-50 border-y border-gray-100">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">

        {/* ── Section Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#F58220]" />
              <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                Why Attend
              </span>
            </motion.div>

            <motion.h2
              {...fadeUp(0.1)}
              className="text-gray-900 text-3xl lg:text-[42px] font-black leading-tight tracking-tight"
            >
              Conference
              <span className="text-[#F58220]"> Highlights</span>
            </motion.h2>

            <motion.p
              {...fadeUp(0.16)}
              className="text-gray-500 text-[15px] mt-3 max-w-xl leading-relaxed"
            >
              Everything that makes SMPACE-2026 a premier platform for civil engineering
              research and innovation.
            </motion.p>
          </div>

          {/* SDG Strip */}
          <motion.div
            {...fadeUp(0.2)}
            className="flex flex-col items-start lg:items-end gap-2 flex-shrink-0"
          >
            <span className="text-gray-400 text-[10px] font-bold tracking-[0.2em] uppercase">
              Aligned with UN SDGs
            </span>
            <div className="flex items-center gap-2">
              {[
                { num: "6",  color: "bg-[#26BDE2]", label: "Clean Water"         },
                { num: "9",  color: "bg-[#F36D25]", label: "Infrastructure"       },
                { num: "11", color: "bg-[#F99D26]", label: "Sustainable Cities"   },
                { num: "12", color: "bg-[#BF8B2E]", label: "Responsible Cons."    },
              ].map((sdg) => (
                <div
                  key={sdg.num}
                  className={`${sdg.color} text-white rounded-lg w-11 h-11 flex flex-col items-center justify-center shadow-sm`}
                  title={sdg.label}
                >
                  <span className="text-[9px] font-bold leading-none opacity-80">SDG</span>
                  <span className="text-[15px] font-black leading-none">{sdg.num}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Cards Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HIGHLIGHTS.map(
            (
              {
                icon: Icon,
                tag, title, desc, stat, statLabel,
                bg, iconBg, iconColor, tagColor,
                titleColor, descColor,
                statColor, statLabelColor,
                borderColor,
              },
              i
            ) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.07 }}
                className={`
                  ${bg} border ${borderColor}
                  rounded-2xl p-6 flex flex-col gap-5
                  shadow-sm hover:shadow-md
                  hover:-translate-y-1
                  transition-all duration-300
                `}
              >
                {/* Top row — icon + tag */}
                <div className="flex items-start justify-between">
                  <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={20} className={iconColor} />
                  </div>
                  <span className={`text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full ${tagColor}`}>
                    {tag}
                  </span>
                </div>

                {/* Title + desc */}
                <div className="flex-1">
                  <h3 className={`${titleColor} text-[17px] font-black leading-tight mb-2`}>
                    {title}
                  </h3>
                  <p className={`${descColor} text-[13px] leading-relaxed`}>
                    {desc}
                  </p>
                </div>

                {/* Bottom stat */}
                <div className={`border-t ${borderColor} pt-4 flex items-baseline gap-2`}>
                  <span className={`${statColor} text-[22px] font-black leading-none`}>
                    {stat}
                  </span>
                  <span className={`${statLabelColor} text-[12px] font-medium`}>
                    {statLabel}
                  </span>
                </div>
              </motion.div>
            )
          )}
        </div>

        {/* ── Bottom CTA Banner ── */}
        <motion.div
          {...fadeUp(0.3)}
          className="mt-14 bg-[#111827] rounded-2xl px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
        >
          <div>
            <p className="text-white text-[18px] font-black leading-tight">
              Ready to be part of SMPACE-2026?
            </p>
            <p className="text-white/50 text-[13px] mt-1">
              Submit your research paper before&nbsp;
              <span className="text-orange-400 font-semibold">June 15, 2026</span>
              &nbsp;·&nbsp;Offline Conference · REVA University, Bengaluru
            </p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <a
              href="mailto:ruceconference@reva.edu.in"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white text-[13px] font-semibold px-5 py-2.5 rounded-lg transition-all duration-200"
            >
              📧 ruceconference@reva.edu.in
            </a>
            <a
              href="/paper-submission"
              className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-6 py-2.5 rounded-lg transition-all duration-200"
            >
              Submit Paper →
            </a>
          </div>
        </motion.div>

      </div>
    </section>
  );
}